import {
  Box,
  Stack,
  Typography,
  useMediaQuery
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  format
} from "date-fns";
import DOMPurify from "dompurify";
import { Lectern } from "lucide-react";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  AttachmentNameTypography,
  AttachmentPill,
  AuthorAvatar,
  BodyTypography,
  ContentStack,
  HeroContentWrapper,
  HeroGridMain,
  OwnerNameTypography,
  SectionTitleTypography,
  SubtitleTypography,
  TitleTypography,
  TopicCardDetailsBox
} from "./TopicCardDetails.style";
import type { TopicCardDetailsProps } from "./TopicCardDetails.types";

import {
  CardCategories,
  CommentsSection,
  CommunityVideo,
  CtaCommunityOverlay,
  GridMain,
  Icon,
  NavigationBreadcrumbs,
  Spinner,
  TopicCardSignature
} from "../../components";
import {
  DarkFill,
  HeroBannerMask,
  MaskWrapper
} from "../../components/HeroBanner/HeroBanner.styles";
import { useElementHeight } from "../../hooks";
import { useTopicQuery } from "../../queries/community";
import { useLanguageStore } from "../../stores";
import { TypographyTextTag } from "../../styles/Typography/TypographyTextTag.styles";
import { stringAvatar } from "../../utils/community";
import { BadgeBox } from "../Commons/BadgeBox";

type TopicAttachment = {
  content: string
  id: string
  name: string
  type: "document" | "link"
};

export function TopicCardDetails({
  id,
  viewComment=false
}: TopicCardDetailsProps & {
  viewComment?: boolean
}) {

  const { t } = useTranslation();

  const theme = useTheme();

  const language = useLanguageStore(state => state.language)?.id;

  const commentsSection = useRef<HTMLDivElement>(null);

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("sm"));
  const isLarge = useMediaQuery(theme.breakpoints.up("lg"));
  const isXLarge = useMediaQuery(theme.breakpoints.up("max"));
  const gradientHookHeight = useElementHeight("gradient-hook");

  const { data: topicData, isLoading } = useTopicQuery({ discussionId: id });
  const scrollToComments = () => {
    const offsetTop = commentsSection?.current?.offsetTop ?? 1000;
    const scrollY = offsetTop - 88 - 40; //to make up for the header's height + gap
    document.scrollingElement?.scrollTo({ behavior: "smooth", top: scrollY });
  };

  useEffect(() => {
    setTimeout(() => {
      if(viewComment) {
        scrollToComments();
      }
    }, 500);
  },[viewComment]);


  const ownerUserId = topicData?.data[0].owner_id;
  const ownerUser = topicData?.enrichedInformation?.users?.find(user => user.owner_id === ownerUserId);
  const cover = topicData?.data[0].attachments?.image?.[0].content;

  const brightcoveVideo = topicData?.data[0]?.attachments?.brightcove?.[0];
  const attachments: TopicAttachment[] = [
    ...(topicData?.data[0]?.attachments?.link ?? []).map((item) => ({
      content: item.content,
      id: item.id,
      name: item.name,
      type: "link" as const
    })),
    ...(topicData?.data[0]?.attachments?.document ?? []).map((item) => ({
      content: item.content,
      id: item.id,
      name: item.name,
      type: "document" as const
    }))
  ];

  const contentStackContent = topicData ? (
    <Stack
      direction={ "column" }
      alignItems={ "start" }
      gap={ "1rem" }
    >
      { /* Title */ }
      <TitleTypography $isRTL={ language === "ar" }>
        { topicData?.data[0].title }
      </TitleTypography>

      { /* Subtitle / Description */ }
      {
        topicData?.data[0]?.subtitle ?
          <SubtitleTypography>
            { topicData?.data[0].subtitle }
          </SubtitleTypography> :
          null
      }

      { /* Topic tags */ }
      <CardCategories
        sx={ {
          "& div":{
            borderColor: theme.customColors.exploreCardText,
            color: theme.customColors.exploreCardText
          },
          width: "100%"
        } }
        categories={ topicData?.data[0].topic.map((cat) => cat.name) ?? [] }
      />

      { /* Date + reading time */ }
      <TopicCardSignature
        created={ getcreatedLabel(topicData?.data[0].publishDate) }
        readingTime={ secondsToMinuteslabel(
          getReadingTime(topicData?.data[0].body)
          + Number(
            topicData?.data[0].attachments?.brightcove?.[0]?.duration ?? "0"
          )
          + Number(
            topicData?.data[0].attachments?.document?.[0]?.duration ?? "0"
          )
        ) }
        size={ "medium" }
        color={ theme.customColors.exploreCardText }
      />

      { /* Like + Comments CTA */ }
      <CtaCommunityOverlay
        id={ id }
        comments={ topicData?.data[0].comment_count }
        likes={ topicData?.data[0].likes }
        commentsOpened={ false }
        commentsClickAction={ scrollToComments }
        selectedFilter={ null }
        selectedTopic={ null }
      />
    </Stack>
  ) : null;

  const authorContent = (
    <Stack
      direction={ "row" }
      alignItems={ "center" }
      gap={ isMobile ? "8px" : "12px" }
    >
      <AuthorAvatar
        alt={ `${ownerUser?.name} ${ownerUser?.surname}` }
        { ...stringAvatar( `${ownerUser?.name} ${ownerUser?.surname}`) }
        src={ ownerUser?.profileImageUrl }
      />
      <OwnerNameTypography>
        { `${ownerUser?.name} ${ownerUser?.surname}` }
      </OwnerNameTypography>
    </Stack>
  );

  return (
    <div id="topicCardDetailContainer" >
      { isLoading ? (
        <Spinner
          size={ 80 }
          thickness={ 3 }
        />
      ):
        !topicData || (topicData && topicData?.data.length == 0) ? (
          <Typography
            tabIndex={ 0 }
            padding={ "42px 80px" }
            textAlign={ "center" }
            color={ theme.customColors.exploreCardText }
          >
            { t("no_data_available") }
          </Typography>
        ):
          <>
            <TopicCardDetailsBox tabIndex={ -1 }>
              <HeroGridMain
                tabIndex={ 0 }
                aria-label={ t("community_post_aria_label",{ subject:ownerUser?.name,title:topicData?.data[0].title }) }
                container
                alignSelf="center"
                flexGrow={ 1 }
                paddingBottom={ 0 }
                $cover={ cover }
              >
                { cover && (
                  <MaskWrapper
                    $isActive={ true }
                    $isTablet={ isTablet }
                    $isLarge={ isLarge }
                    $isXLarge={ isXLarge }
                    sx={ {
                      width: "48%"
                    } }
                  >
                    <DarkFill
                      $isActive={ true }
                      $isTablet={ isTablet }
                      $gradientHookHeight={ gradientHookHeight }
                      sx={ {
                        ...(isTablet && {
                          left:  "-57%" 
                        }),
                        ...((!isTablet) && {
                          left:  "-42%" 
                        })
                      } }
                    />
                    <HeroBannerMask
                      $isActive={ true }
                      $isTablet={ isTablet }
                      $gradientHookHeight={ gradientHookHeight }
                      sx={ {
                        ...((isTablet) && {
                          left: "48%",
                          width: "60%"
                        })
                      } }
                    />
                  </MaskWrapper>
                ) }

                <HeroContentWrapper
                  aria-hidden={ true }
                  tabIndex={ -1 }>
                  <Box
                    sx={ {
                      marginBottom: "22px",
                      "& nav": {
                        paddingLeft: 0
                      }
                    } }
                  >
                    <NavigationBreadcrumbs
                      ariaHidden={ true }
                      tabIndex={ -1 }
                      learningObjectTypology="COURSE"
                      previousPath="/community"
                      activeLabel={ t("talk") }
                    />
                  </Box>

                  <BadgeBox
                    background={ theme.customColors.backgroundSecondary }
                    textColor={ "inherit" }
                    border={ `1px solid ${theme.customColors.textTag}` }>
                    <Stack
                      direction={ "row" }
                      alignItems={ "center" }
                      gap={ "8px" }
                    >
                      <Lectern size={ 16 } color={ theme.customColors.textTag }/>
                      <TypographyTextTag>
                        { t("talk") }
                      </TypographyTextTag>
                    </Stack>
                  </BadgeBox>

                  { /* Author avatar + name (desktop: dentro l'hero) */ }
                  { !isMobile && authorContent }

                  { !isMobile &&
                    <ContentStack
                      gap={ "2rem" }
                      position={ "relative" }
                      width={ "42%" }
                    >
                      { contentStackContent }
                    </ContentStack>
                  }

                </HeroContentWrapper>
              </HeroGridMain>
              <GridMain
                paddingTop={ isMobile ? "1.5rem" : "3rem" }
              >
                <Stack
                  direction={ "column" }
                  width={ "100%" }
                >
                  { /* Author avatar + name (mobile: sotto il border dell'hero) */ }
                  { isMobile &&
                    <Stack marginBottom={ "1.5rem" }>
                      { authorContent }
                    </Stack>
                  }
                  { isMobile &&
                    <ContentStack
                      gap={ "2rem" }
                      position={ "relative" }
                      width={ "100%" }
                      marginBottom={ "1.5rem" }
                    >
                      { contentStackContent }
                    </ContentStack>
                  }
                  { topicData?.data[0].body ?
                    <BodyTypography
                      dangerouslySetInnerHTML={ { __html: DOMPurify.sanitize(topicData?.data[0].body ?? "", { ADD_ATTR: ["target"] }) } }
                      variant="h1"
                    />
                    : null
                  }

                  { /* Video section */ }
                  { brightcoveVideo &&
                    <Stack
                      gap={ "1rem" }
                      width={ "100%" }
                      position={ "relative" }
                      marginTop={ "2.5rem" }
                    >
                      <SectionTitleTypography>
                        { t("talk_video") }
                      </SectionTitleTypography>
                      <Box
                        sx={ {
                          backgroundColor: "#000000",
                          width: "100%",
                          height: isMobile ? "auto" : isXLarge ? "540px" : "474px",
                          aspectRatio: isMobile ? "16 / 9" : "auto",
                          maxHeight: isXLarge ? "540px" : "474px"
                        } }
                      >
                        <CommunityVideo
                          height={ "100%" }
                          id={ Number(brightcoveVideo.id) || 1 }
                          title={ brightcoveVideo.name }
                          videoId={ brightcoveVideo.content }
                        />
                      </Box>
                    </Stack>
                  }

                  { /* Attachments section */ }
                  { attachments.length > 0 &&
                    <Stack
                      gap={ "1rem" }
                      width={ "100%" }
                      marginTop={ "2.5rem" }
                      paddingBottom={ "1.5rem" }
                    >
                      <SectionTitleTypography>
                        { t("talk_attachments") }
                      </SectionTitleTypography>
                      <Box
                        display={ "grid" }
                        gap={ "1rem" }
                        gridTemplateColumns={ isMobile ? "1fr" : "repeat(2, 1fr)" }
                        width={ "100%" }
                      >
                        { attachments.map((attachment) => (
                          <AttachmentPill
                            key={ `${attachment.type}-${attachment.id}` }
                            onClick={ () => handleAttachmentClick(attachment) }
                          >
                            <Icon
                              icon={ attachment.type === "link" ? "Icons_link" : "Icons_File" }
                              width={ "1rem" }
                              height={ "1rem" }
                              color={ theme.customColors.textPrimary }
                            />
                            <AttachmentNameTypography>
                              { attachment.name }
                            </AttachmentNameTypography>
                            { attachment.type === "document" &&
                              <Icon
                                icon={ "download" }
                                width={ "1rem" }
                                height={ "1rem" }
                                color={ theme.customColors.textPrimary }
                              />
                            }
                          </AttachmentPill>
                        )) }
                      </Box>
                    </Stack>
                  }
                </Stack>
              </GridMain>

              <GridMain>
                <Stack
                  ref={ commentsSection }
                  width={ "100%" }
                  position={ "relative" }
                  paddingBottom={ "24px" }
                  gap={ "1rem" }
                >
                  <SectionTitleTypography>
                    { t("talk_comments") }
                  </SectionTitleTypography>
                  <CommentsSection
                    topicOwnerId={ topicData.data?.[0]?.owner_id ?? "0" }
                    selectedFilter={ null }
                    selectedTopic={ null }
                    discussionId={ id }
                    withInputPadding
                    className="visible" />
                </Stack>
              </GridMain>
            </TopicCardDetailsBox>
          </>

      }
    </div>
  );

  function handleAttachmentClick(attachment: TopicAttachment) {
    if(!attachment.content) {
      return;
    }
    if(attachment.type === "link") {
      window.open(attachment.content, "_blank", "noopener,noreferrer");
      return;
    }
    const anchor = document.createElement("a");
    anchor.href = attachment.content;
    anchor.download = attachment.name;
    anchor.target = "_blank";
    anchor.rel = "noreferrer";
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  }

  function getcreatedLabel(created: string) {
    if(created) {
      const date = new Date(created);
      const now = new Date();
      const hours = Math.trunc(((now.getTime() - date.getTime()) / 1000 / 3600));
      if (hours < 1) return t("now");
      if (hours < 24) return `${t("hour", { count: hours })} ${t("ago")}`;
      if (hours < 48) return `${t("day", { count: 1 })} ${t("ago")}`;
      if (hours < 168) return `${t("day", { count: Math.floor(hours / 24) })} ${t("ago")}`;
      if (hours < 336) return `${t("week", { count: 1 })} ${t("ago")}`;
      else return format(date, "dd/MM/yyy");
    } else {
      return "undefined";
    }

  }

  function getReadingTime(text: string | null | undefined) {
    if (!text) return 0;
    const words = text.split(" ").length;
    return words / 2;  //2 words per second
  }

  function secondsToMinuteslabel(duration: number) {
    if(duration < 60) {
      return `${Math.round(duration)} sec`;
    } else {
      return `${Math.round(duration / 60)} min`;
    }
  }

}
